import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/utils/errors';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { teamApi } from '@/services/api';
import { Repository } from '@/types';

interface RepositoriesTableProps {
  teamId: string;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

type Platform = 'github' | 'gitlab';

const EMPTY_FORM = {
  url: '',
  name: '',
  platform: 'github' as Platform,
  default_branch: 'main',
  language: '',
  is_primary: false,
  auto_create_jira: false,
};

export default function RepositoriesTable({ teamId, onNotify }: RepositoriesTableProps) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Repository | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const { data: repositories } = useQuery({
    queryKey: ['team-repositories', teamId],
    queryFn: () => teamApi.getRepositories(teamId).then((res) => res.data),
    enabled: !!teamId,
  });

  const addMutation = useMutation({
    mutationFn: (data: Partial<Repository>) => teamApi.addRepository(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-repositories', teamId] });
      onNotify('Repository added successfully!', 'success');
      handleClose();
    },
    onError: (error: Error) => {
      onNotify(`Failed to add repository: ${getErrorMessage(error)}`, 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Repository> }) =>
      teamApi.updateRepository(teamId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-repositories', teamId] });
      onNotify('Repository updated successfully!', 'success');
      handleClose();
    },
    onError: (error: Error) => {
      onNotify(`Failed to update repository: ${getErrorMessage(error)}`, 'error');
    },
  });

  const removeRepoMutation = useMutation({
    mutationFn: (repoId: string) => teamApi.removeRepository(teamId, repoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-repositories', teamId] });
      onNotify('Repository removed successfully!', 'success');
    },
    onError: (error: Error) => {
      onNotify(`Failed to remove repository: ${getErrorMessage(error)}`, 'error');
    },
  });

  const handleOpen = (item?: Repository) => {
    if (item) {
      setEditingItem(item);
      setForm({
        url: item.url,
        name: item.name || '',
        platform: item.platform || 'github',
        default_branch: item.default_branch || 'main',
        language: item.language || '',
        is_primary: item.is_primary || false,
        auto_create_jira: item.auto_create_jira || false,
      });
    } else {
      setEditingItem(null);
      setForm(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = () => {
    const data: Partial<Repository> = {
      url: form.url,
      name: form.name || undefined,
      platform: form.platform,
      default_branch: form.default_branch || undefined,
      language: form.language || undefined,
      is_primary: form.is_primary,
      auto_create_jira: form.auto_create_jira,
    };
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      addMutation.mutate(data);
    }
  };

  const handleRemove = (repoId: string) => {
    if (window.confirm('Are you sure you want to remove this repository?')) {
      removeRepoMutation.mutate(repoId);
    }
  };

  const isSaving = addMutation.isPending || updateMutation.isPending;

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Repository
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Language</TableCell>
              <TableCell>Primary</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {repositories?.map((repo) => (
              <TableRow key={repo.id}>
                <TableCell>
                  <a href={repo.url} target="_blank" rel="noopener noreferrer">
                    {repo.name || repo.url}
                  </a>
                </TableCell>
                <TableCell>
                  <Chip label={repo.platform} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{repo.language || '-'}</TableCell>
                <TableCell>
                  {repo.is_primary && <Chip label="Primary" size="small" color="primary" />}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit repository">
                    <IconButton size="small" onClick={() => handleOpen(repo)} sx={{ mr: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove repository">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemove(repo.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {repositories?.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No repositories added yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Repository' : 'Add Repository'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="URL"
              value={form.url}
              onChange={(e) => setForm((prev) => ({ ...prev, url: e.target.value }))}
              required
              fullWidth
              placeholder="https://github.com/org/repo"
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Platform</InputLabel>
              <Select
                value={form.platform}
                label="Platform"
                onChange={(e) => setForm((prev) => ({ ...prev, platform: e.target.value as Platform }))}
              >
                <MenuItem value="github">GitHub</MenuItem>
                <MenuItem value="gitlab">GitLab</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Default Branch"
              value={form.default_branch}
              onChange={(e) => setForm((prev) => ({ ...prev, default_branch: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Language"
              value={form.language}
              onChange={(e) => setForm((prev) => ({ ...prev, language: e.target.value }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_primary}
                  onChange={(e) => setForm((prev) => ({ ...prev, is_primary: e.target.checked }))}
                />
              }
              label="Primary repository"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.auto_create_jira}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, auto_create_jira: e.target.checked }))
                  }
                />
              }
              label="Auto-create JIRA tickets for new issues"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || !form.url.trim()}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
